import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Download, ChevronDown, ChevronUp, CheckCircle, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'self-publishing', label: 'Self-Publishing' },
  { id: 'audiobook', label: 'Audiobook' },
  { id: 'podcast', label: 'Podcast' },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const toCsv = (leads) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = leads.map((l) => [fmtDate(l.created_at), l.name, l.email, l.service_label, l.total_label,
    l.line_items.map((li) => `${li.label}${li.detail ? ` (${li.detail})` : ''}${li.amount ? `: ${li.amount}` : ''}`).join(' | '),
    l.email_sent ? 'yes' : 'no', l.contacted ? 'yes' : 'no'].map(esc).join(','));
  return ['Date,Name,Email,Service,Ballpark,Selections,Quote emailed,Contacted', ...rows].join('\n');
};

const LeadRow = ({ lead, open, onToggle, onContacted }) => (
  <>
    <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`estimator-lead-row-${lead.id}`}>
      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{fmtDate(lead.created_at)}</td>
      <td className="px-4 py-3 text-sm">
        <div className="font-medium text-gray-900">{lead.name || '—'}</div>
        <a href={`mailto:${lead.email}`} className="text-[#A7468A] hover:underline" data-testid={`estimator-lead-email-${lead.id}`}>{lead.email}</a>
      </td>
      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 rounded-full text-xs font-semibold bg-[#A7468A]/10 text-[#A7468A]">{lead.service_label}</span></td>
      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{lead.total_label}</td>
      <td className="px-4 py-3 text-sm">
        {lead.email_sent
          ? <span className="inline-flex items-center gap-1 text-green-700"><Mail className="w-3.5 h-3.5" /> Sent</span>
          : <span className="text-amber-600">Not sent</span>}
      </td>
      <td className="px-4 py-3 text-sm">
        <button onClick={() => onContacted(lead)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${lead.contacted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`estimator-lead-contacted-${lead.id}`}>
          <CheckCircle className="w-3.5 h-3.5" /> {lead.contacted ? 'Contacted' : 'Mark contacted'}
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        <button onClick={onToggle} className="text-gray-500 hover:text-gray-900" aria-label="Toggle details" data-testid={`estimator-lead-expand-${lead.id}`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </td>
    </tr>
    {open && (
      <tr className="bg-gray-50 border-b border-gray-100" data-testid={`estimator-lead-details-${lead.id}`}>
        <td colSpan={7} className="px-6 py-4">
          <ul className="space-y-1 text-sm text-gray-700">
            {lead.line_items.map((li, i) => (
              <li key={i} className="flex justify-between gap-4 max-w-xl">
                <span>{li.label}{li.detail ? <span className="text-gray-500"> · {li.detail}</span> : null}</span>
                <span className="font-medium">{li.amount}</span>
              </li>
            ))}
          </ul>
          {lead.notes && <p className="mt-3 text-sm text-gray-600"><b>Notes:</b> {lead.notes}</p>}
        </td>
      </tr>
    )}
  </>
);

export const EstimatorLeadsPanel = () => {
  const [data, setData] = useState({ leads: [], total: 0, by_service: {} });
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/estimator-quote/leads`, { headers: authHeaders(), params: filter ? { service: filter } : {} });
      setData(res.data);
    } catch (e) {
      toast.error('Could not load quote leads');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggleContacted = async (lead) => {
    try {
      await axios.patch(`${API}/estimator-quote/leads/${lead.id}`, { contacted: !lead.contacted }, { headers: authHeaders() });
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === lead.id ? { ...l, contacted: !l.contacted } : l)) }));
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(data.leads)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `estimator-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="admin-estimator-tab">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quote Leads</h2>
          <p className="text-gray-600 text-sm">Visitors who emailed themselves a PDF quote from the pricing estimators.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50" data-testid="estimator-leads-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={exportCsv} disabled={!data.leads.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A7468A] text-white text-sm hover:bg-[#BC516F] disabled:opacity-50" data-testid="estimator-leads-export">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4" data-testid="estimator-leads-total"><p className="text-xs text-gray-500 uppercase">Total</p><p className="text-2xl font-bold text-gray-900">{data.total}</p></div>
        {FILTERS.slice(1).map((f) => (
          <div key={f.id} className="bg-white rounded-lg shadow p-4" data-testid={`estimator-leads-count-${f.id}`}><p className="text-xs text-gray-500 uppercase">{f.label}</p><p className="text-2xl font-bold text-gray-900">{data.by_service[f.id] || 0}</p></div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.id || 'all'} onClick={() => setFilter(f.id)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === f.id ? 'bg-[#A7468A] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-testid={`estimator-leads-filter-${f.id || 'all'}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-[820px]" data-testid="estimator-leads-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Lead</th><th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Ballpark</th><th className="px-4 py-3">Quote</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {data.leads.map((l) => (
              <LeadRow key={l.id} lead={l} open={openId === l.id} onToggle={() => setOpenId(openId === l.id ? null : l.id)} onContacted={toggleContacted} />
            ))}
            {!data.leads.length && !loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500" data-testid="estimator-leads-empty">No quote leads yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EstimatorLeadsPanel;
